import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, Camera, Upload, Info, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
type ReferencePhoto = {
  id: string;
  roomKey: string;
  photoUrl: string;
  updatedAt: string;
};
// Mock data - multiple photos per room
const initialPhotos: ReferencePhoto[] = [{
  id: '1',
  roomKey: 'soggiorno',
  photoUrl: 'https://a0.muscache.com/im/pictures/prohost-api/Hosting-1353673811838304349/original/142376af-a8ad-457c-a3af-4b264a5e291c.jpeg?aki_policy=xx_large',
  updatedAt: '15 Gen 2025'
}, {
  id: '1b',
  roomKey: 'soggiorno',
  photoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/22e98b9a-4848-48d2-8bc9-f75adab5a84d.jpeg?im_w=1200',
  updatedAt: '15 Gen 2025'
}, {
  id: '2',
  roomKey: 'cucina',
  photoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/22e98b9a-4848-48d2-8bc9-f75adab5a84d.jpeg?im_w=1200',
  updatedAt: '15 Gen 2025'
}, {
  id: '3',
  roomKey: 'bagno',
  photoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/8da17c5b-c2d1-4b3e-aa33-a0df921816fa.jpeg?aki_policy=xx_large',
  updatedAt: '10 Gen 2025'
}, {
  id: '4',
  roomKey: 'camera1',
  photoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/731e18a0-32d0-464a-ba59-ab8afb80ae73.jpeg?aki_policy=xx_large',
  updatedAt: '10 Gen 2025'
}, {
  id: '5',
  roomKey: 'camera2',
  photoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/c900d0e5-5343-48d7-aad9-a831c90712e7.jpeg?aki_policy=xx_large',
  updatedAt: '10 Gen 2025'
}, {
  id: '6',
  roomKey: 'ingresso',
  photoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-U3RheVN1cHBseUxpc3Rpbmc6MTM0MDM1MzQyMDY5ODk1MTY1NA%3D%3D/original/bfe5b7dc-812b-4978-a225-9a4a74e37e4c.jpeg?aki_policy=xx_large',
  updatedAt: '10 Gen 2025'
}];
type ModalType = 'delete' | 'add' | 'selectRoom' | 'gallery' | null;
export function ReferencePhotosPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<ReferencePhoto[]>(initialPhotos);
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<ReferencePhoto | null>(null);
  const [selectedRoomForAdd, setSelectedRoomForAdd] = useState<string>('');
  const [newRoomName, setNewRoomName] = useState('');
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryPhotos, setGalleryPhotos] = useState<ReferencePhoto[]>([]);
  // Group photos by room
  const photosByRoom = photos.reduce((acc, photo) => {
    if (!acc[photo.roomKey]) {
      acc[photo.roomKey] = [];
    }
    acc[photo.roomKey].push(photo);
    return acc;
  }, {} as Record<string, ReferencePhoto[]>);
  // Get unique room keys
  const existingRooms = Object.keys(photosByRoom);
  const handleDelete = (photo: ReferencePhoto) => {
    setSelectedPhoto(photo);
    setModalType('delete');
  };
  const handleConfirmDelete = () => {
    if (selectedPhoto) {
      setPhotos(photos.filter(p => p.id !== selectedPhoto.id));
    }
    setModalType(null);
    setSelectedPhoto(null);
  };
  const handleAddNew = () => {
    setModalType('selectRoom');
  };
  const handleSelectExistingRoom = (roomKey: string) => {
    setSelectedRoomForAdd(roomKey);
    setModalType('add');
  };
  const handleCreateNewRoom = () => {
    setSelectedRoomForAdd('new');
    setModalType('add');
  };
  const handleSaveNew = () => {
    const roomKey = selectedRoomForAdd === 'new' ? newRoomName.toLowerCase().replace(/\s+/g, '-') : selectedRoomForAdd;
    if (roomKey) {
      const newPhoto: ReferencePhoto = {
        id: `new-${Date.now()}`,
        roomKey,
        photoUrl: 'https://via.placeholder.com/400x300?text=New+Photo',
        updatedAt: 'Oggi'
      };
      setPhotos([...photos, newPhoto]);
      setNewRoomName('');
      setSelectedRoomForAdd('');
      setModalType(null);
    }
  };
  const handleTakePhoto = () => {
    console.log('Opening camera...');
    handleSaveNew();
  };
  const handleUploadPhoto = () => {
    console.log('Opening gallery...');
    handleSaveNew();
  };
  const handleOpenGallery = (roomKey: string, photoIndex: number) => {
    const roomPhotos = photosByRoom[roomKey];
    setGalleryPhotos(roomPhotos);
    setGalleryIndex(photoIndex);
    setModalType('gallery');
  };
  const handleGalleryPrev = () => {
    setGalleryIndex(prev => prev > 0 ? prev - 1 : galleryPhotos.length - 1);
  };
  const handleGalleryNext = () => {
    setGalleryIndex(prev => prev < galleryPhotos.length - 1 ? prev + 1 : 0);
  };
  const getRoomName = (roomKey: string) => {
    const rooms = t.host.referencePhotos.rooms as Record<string, string>;
    return rooms[roomKey] || roomKey;
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1196">
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-1197">
        {/* Header */}
        <motion.div className="flex items-center justify-between mb-6" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1198">
          <div className="flex items-center gap-4" data-id="element-1199">
            <button onClick={() => navigate('/host')} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-1200">
              <ArrowLeft className="w-6 h-6 text-gray-600" data-id="element-1201" />
            </button>
            <h1 className="text-2xl font-bold text-navy" data-id="element-1202">
              {t.host.referencePhotos.title}
            </h1>
          </div>

          <Button onClick={handleAddNew} className="gap-2" data-id="element-1203">
            <Plus className="w-5 h-5" data-id="element-1204" />
            {t.host.referencePhotos.add}
          </Button>
        </motion.div>

        {/* Info Box */}
        <motion.div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl mb-8" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} data-id="element-1205">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1206" />
          <p className="text-sm text-blue-800" data-id="element-1207">
            {t.host.referencePhotos.infoText}
          </p>
        </motion.div>

        {/* Photos by Room */}
        {existingRooms.length > 0 ? <div className="space-y-8" data-id="element-1208">
            {existingRooms.map((roomKey, roomIndex) => <motion.div key={roomKey} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1 + roomIndex * 0.05
        }} data-id="element-1209">
                <div className="flex items-center justify-between mb-4" data-id="element-1210">
                  <h2 className="text-lg font-bold text-navy" data-id="element-1211">
                    {getRoomName(roomKey)}
                  </h2>
                  <button onClick={() => handleSelectExistingRoom(roomKey)} className="text-sm text-teal-deep hover:text-teal-700 font-medium flex items-center gap-1" data-id="element-1212">
                    <Plus className="w-4 h-4" data-id="element-1213" />
                    Aggiungi foto
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1214">
                  {photosByRoom[roomKey].map((photo, photoIndex) => <Card key={photo.id} className="overflow-hidden group relative" data-id="element-1215">
                      <div className="aspect-[4/3] cursor-pointer" onClick={() => handleOpenGallery(roomKey, photoIndex)} data-id="element-1216">
                        <img src={photo.photoUrl} alt={`${getRoomName(roomKey)} ${photoIndex + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-1217" />
                      </div>

                      {/* Delete button */}
                      <button onClick={e => {
                e.stopPropagation();
                handleDelete(photo);
              }} className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100" data-id="element-1218">
                        <Trash2 className="w-4 h-4 text-red-600" data-id="element-1219" />
                      </button>
                    </Card>)}
                </div>
              </motion.div>)}
          </div> :
      // Empty State
      <motion.div className="text-center py-16" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} data-id="element-1220">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1221">
              <Camera className="w-10 h-10 text-gray-400" data-id="element-1222" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1223">
              {t.host.referencePhotos.emptyState.title}
            </h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto" data-id="element-1224">
              {t.host.referencePhotos.emptyState.subtitle}
            </p>
            <Button onClick={handleAddNew} data-id="element-1225">
              {t.host.referencePhotos.emptyState.cta}
            </Button>
          </motion.div>}
      </main>

      {/* Modals */}
      <AnimatePresence data-id="element-1226">
        {modalType && <motion.div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setModalType(null)} data-id="element-1227">
            <motion.div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} data-id="element-1228">
              {/* Select Room Modal */}
              {modalType === 'selectRoom' && <div className="p-6" data-id="element-1229">
                  <div className="flex items-center justify-between mb-6" data-id="element-1230">
                    <h3 className="text-lg font-bold text-navy" data-id="element-1231">
                      Seleziona Ambiente
                    </h3>
                    <button onClick={() => setModalType(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1232">
                      <X className="w-5 h-5 text-gray-500" data-id="element-1233" />
                    </button>
                  </div>

                  <div className="space-y-3" data-id="element-1234">
                    {existingRooms.map(roomKey => <button key={roomKey} onClick={() => handleSelectExistingRoom(roomKey)} className="w-full p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-left" data-id="element-1235">
                        <span className="font-medium text-gray-900" data-id="element-1236">
                          {getRoomName(roomKey)}
                        </span>
                        <span className="text-sm text-gray-500 ml-2" data-id="element-1237">
                          ({photosByRoom[roomKey].length} foto)
                        </span>
                      </button>)}

                    <button onClick={handleCreateNewRoom} className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-teal-deep hover:bg-teal-50/30 transition-colors text-left" data-id="element-1238">
                      <div className="flex items-center gap-2" data-id="element-1239">
                        <Plus className="w-5 h-5 text-teal-deep" data-id="element-1240" />
                        <span className="font-medium text-teal-deep" data-id="element-1241">
                          Crea nuovo ambiente
                        </span>
                      </div>
                    </button>
                  </div>
                </div>}

              {/* Delete Modal */}
              {modalType === 'delete' && selectedPhoto && <div className="p-6" data-id="element-1242">
                  <h3 className="text-lg font-bold text-navy mb-2" data-id="element-1243">
                    {t.host.referencePhotos.deleteTitle}
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-1244">
                    Sei sicuro di voler eliminare questa foto?
                  </p>
                  <div className="flex gap-3" data-id="element-1245">
                    <Button variant="secondary" onClick={() => setModalType(null)} className="flex-1" data-id="element-1246">
                      {t.host.referencePhotos.cancel}
                    </Button>
                    <button onClick={handleConfirmDelete} className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors" data-id="element-1247">
                      {t.host.referencePhotos.delete}
                    </button>
                  </div>
                </div>}

              {/* Add Photo Modal */}
              {modalType === 'add' && <div className="p-6" data-id="element-1248">
                  <div className="flex items-center justify-between mb-6" data-id="element-1249">
                    <h3 className="text-lg font-bold text-navy" data-id="element-1250">
                      {selectedRoomForAdd === 'new' ? 'Nuovo Ambiente' : getRoomName(selectedRoomForAdd)}
                    </h3>
                    <button onClick={() => setModalType(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1251">
                      <X className="w-5 h-5 text-gray-500" data-id="element-1252" />
                    </button>
                  </div>

                  <div className="space-y-4" data-id="element-1253">
                    {selectedRoomForAdd === 'new' && <div data-id="element-1254">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1255">
                          Nome ambiente
                        </label>
                        <input type="text" value={newRoomName} onChange={e => setNewRoomName(e.target.value)} placeholder="Es. Giardino, Terrazzo..." className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" data-id="element-1256" />
                      </div>}

                    <div className="space-y-3" data-id="element-1257">
                      <button onClick={handleTakePhoto} disabled={selectedRoomForAdd === 'new' && !newRoomName.trim()} className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1258">
                        <Camera className="w-6 h-6 text-teal-deep" data-id="element-1259" />
                        <span className="font-medium text-gray-900" data-id="element-1260">
                          {t.host.referencePhotos.takeNewPhoto}
                        </span>
                      </button>

                      <button onClick={handleUploadPhoto} disabled={selectedRoomForAdd === 'new' && !newRoomName.trim()} className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1261">
                        <Upload className="w-6 h-6 text-teal-deep" data-id="element-1262" />
                        <span className="font-medium text-gray-900" data-id="element-1263">
                          {t.host.referencePhotos.uploadFromGallery}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>}
            </motion.div>
          </motion.div>}

        {/* Gallery Modal - Full Screen */}
        {modalType === 'gallery' && <motion.div className="fixed inset-0 bg-black z-50 flex items-center justify-center" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-1264">
            {/* Close button */}
            <button onClick={() => setModalType(null)} className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10" data-id="element-1265">
              <X className="w-6 h-6 text-white" data-id="element-1266" />
            </button>

            {/* Navigation buttons */}
            {galleryPhotos.length > 1 && <>
                <button onClick={handleGalleryPrev} className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10" data-id="element-1267">
                  <ChevronLeft className="w-6 h-6 text-white" data-id="element-1268" />
                </button>
                <button onClick={handleGalleryNext} className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10" data-id="element-1269">
                  <ChevronRight className="w-6 h-6 text-white" data-id="element-1270" />
                </button>
              </>}

            {/* Image */}
            <motion.img key={galleryIndex} src={galleryPhotos[galleryIndex]?.photoUrl} alt="Gallery" className="max-w-full max-h-full object-contain" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2
        }} data-id="element-1271" />

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full" data-id="element-1272">
              <span className="text-white text-sm font-medium" data-id="element-1273">
                {galleryIndex + 1} / {galleryPhotos.length}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}